import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../components/Icon';
import { api, ApiError } from '../lib/api';
import { kycTone, RISK_LABEL, RISK_OPTIONS, riskTone } from '../lib/crm';
import { amount, initials, tone } from '../lib/format';
import { useSession } from '../lib/session';
import '../styles/crm.css';

interface Counterparty {
  id: string;
  name: string;
  kinds: string[];
  kindLabels: string[];
  country: string | null;
  taxNumber: string | null;
  currency: string;
  paymentTermsDays: number;
  creditLimitMinor: number | null;
  kycStatus: string;
  kycLabel: string;
  riskLevel: string;
  visibility: string;
  shared: boolean;
  documents: number;
  billedMinor: number;
  outstandingMinor: number;
  creditExceeded: boolean;
}

const KIND_OPTIONS: Array<{ code: string; label: string }> = [
  { code: 'client', label: 'Cliente' },
  { code: 'supplier', label: 'Proveedor' },
  { code: 'buyer', label: 'Comprador' },
  { code: 'seller', label: 'Vendedor' },
  { code: 'broker', label: 'Broker' },
  { code: 'agent', label: 'Agente' },
  { code: 'carrier', label: 'Transportista' },
  { code: 'bank', label: 'Banco' },
  { code: 'insurer', label: 'Aseguradora' },
  { code: 'consultant', label: 'Consultor' },
  { code: 'partner', label: 'Partner' },
];

function CreateModal({ defaultCurrency, onClose, onCreated }: { defaultCurrency: string; onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState('client');
  const [country, setCountry] = useState('AE');
  const [taxNumber, setTaxNumber] = useState('');
  const [currency, setCurrency] = useState(defaultCurrency);
  const [terms, setTerms] = useState('30');
  const [limit, setLimit] = useState('');
  const [risk, setRisk] = useState('low');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [zip, setZip] = useState('');
  const [cName, setCName] = useState('');
  const [cPhone, setCPhone] = useState('');
  const [cEmail, setCEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (name.trim().length < 2) { setErr('El nombre es obligatorio.'); return; }
    setBusy(true);
    setErr(null);
    try {
      const contacts = cName.trim()
        ? [{ name: cName.trim(), phone: cPhone.trim() || undefined, emails: cEmail.trim() ? [cEmail.trim()] : [], isPrimary: true }]
        : undefined;
      await api.post('/counterparties', {
        name: name.trim(),
        kinds: [kind],
        country: country.trim() || undefined,
        address: address.trim() || undefined,
        city: city.trim() || undefined,
        postalCode: zip.trim() || undefined,
        taxNumber: taxNumber.trim() || undefined,
        currency: currency.trim() || defaultCurrency,
        paymentTermsDays: Number(terms) || 0,
        creditLimitMinor: limit.trim() === '' ? undefined : Math.round(Number(limit) * 100),
        riskLevel: risk,
        visibility: 'company',
        contacts,
      });
      onCreated();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'No se pudo crear la contraparte.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-scrim" onMouseDown={onClose}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal__head">
          <div>
            <span className="eyebrow">Nueva</span>
            <h2 className="h2" style={{ marginTop: 4, fontSize: 20 }}>Alta de contraparte</h2>
          </div>
          <button className="icon-btn" onClick={onClose}><Icon name="x" size={18} /></button>
        </div>

        <div className="modal__body">
          <div className="field"><label>Name or legal name</label><input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="p. ej. TechCorp FZE" autoFocus /></div>

          <div className="field">
            <label>Relationship type</label>
            <div className="row gap-8" style={{ marginBottom: 8 }}>
              {['client', 'supplier'].map((k) => (
                <button
                  key={k}
                  type="button"
                  className={`btn btn--sm ${kind === k ? 'btn--primary' : 'btn--ghost'}`}
                  onClick={() => setKind(k)}
                >
                  {k === 'client' ? 'Cliente' : 'Proveedor'}
                </button>
              ))}
              <span className="faint" style={{ fontSize: 12, alignSelf: 'center' }}>u otro tipo:</span>
            </div>
            <select className="select" value={kind} onChange={(e) => setKind(e.target.value)}>
              {KIND_OPTIONS.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
            </select>
          </div>

          <div className="row gap-12">
            <div className="field grow" style={{ minWidth: 120 }}><label>Country</label><input className="input" value={country} onChange={(e) => setCountry(e.target.value)} placeholder="AE" /></div>
            <div className="field grow" style={{ minWidth: 160 }}><label>Nº fiscal (TRN)</label><input className="input" value={taxNumber} onChange={(e) => setTaxNumber(e.target.value)} /></div>
            <div className="field" style={{ width: 100 }}><label>Moneda</label><input className="input" value={currency} onChange={(e) => setCurrency(e.target.value)} /></div>
          </div>

          <div className="row gap-12">
            <div className="field" style={{ width: 140 }}><label>Payment term (days)</label><input className="input" type="number" value={terms} onChange={(e) => setTerms(e.target.value)} /></div>
            <div className="field grow" style={{ minWidth: 140 }}><label>Credit limit</label><input className="input" type="number" value={limit} onChange={(e) => setLimit(e.target.value)} placeholder="No limit" /></div>
            <div className="field" style={{ width: 150 }}>
              <label>Riesgo</label>
              <select className="select" value={risk} onChange={(e) => setRisk(e.target.value)}>
                {RISK_OPTIONS.map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
              </select>
            </div>
          </div>

          <div className="field"><label>Address</label><input className="input" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Street, building, office…" /></div>
          <div className="row gap-12">
            <div className="field grow" style={{ minWidth: 140 }}><label>Ciudad</label><input className="input" value={city} onChange={(e) => setCity(e.target.value)} /></div>
            <div className="field" style={{ width: 130 }}><label>Postal code</label><input className="input" value={zip} onChange={(e) => setZip(e.target.value)} /></div>
          </div>

          <div className="eyebrow" style={{ marginTop: 6, marginBottom: 8 }}>Persona de contacto (opcional)</div>
          <div className="row gap-12">
            <div className="field grow" style={{ minWidth: 140 }}><label>Nombre</label><input className="input" value={cName} onChange={(e) => setCName(e.target.value)} /></div>
            <div className="field grow" style={{ minWidth: 130 }}><label>Phone</label><input className="input" value={cPhone} onChange={(e) => setCPhone(e.target.value)} /></div>
          </div>
          <div className="field"><label>Email</label><input className="input" value={cEmail} onChange={(e) => setCEmail(e.target.value)} placeholder="persona@empresa.com" /></div>
          <p className="faint" style={{ fontSize: 12, marginTop: -4 }}>You can add more contacts and bank accounts in the profile.</p>

          {err && <div className="act-error"><Icon name="alert" size={16} />{err}</div>}
        </div>

        <div className="modal__foot">
          <button className="btn btn--ghost" onClick={onClose}>Cancelar</button>
          <button className="btn btn--primary" onClick={submit} disabled={busy}>{busy ? 'Creando…' : 'Crear contraparte'}</button>
        </div>
      </div>
    </div>
  );
}

export function Counterparties() {
  const { company } = useSession();
  const [rows, setRows] = useState<Counterparty[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const nav = useNavigate();

  const load = () => {
    if (!company) return;
    setRows(null);
    api.get<Counterparty[]>('/counterparties').then(setRows).catch((e) => setErr(e instanceof Error ? e.message : 'Error'));
  };
  useEffect(load, [company]);

  const filtered = useMemo(
    () => (rows ?? []).filter((c) => !q || c.name.toLowerCase().includes(q.toLowerCase()) || (c.country ?? '').toLowerCase().includes(q.toLowerCase())),
    [rows, q],
  );

  return (
    <main className="main">
      <div className="page-head">
        <div className="page-head__row">
          <div className="page-head__title">
            <span className="eyebrow">Relaciones</span>
            <h1 className="h1">Clientes y proveedores</h1>
            <p className="page-head__sub">Your counterparty portfolio, with KYC status, risk level and credit exposure.</p>
          </div>
          <button className="btn btn--primary" onClick={() => setCreating(true)}><Icon name="plus" size={16} />Nueva contraparte</button>
        </div>
      </div>

      <div className="search" style={{ maxWidth: 360, height: 40, marginBottom: 16 }}>
        <Icon name="search" size={17} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by name or country…" />
      </div>

      <div className="card">
        {err && <div className="empty">{err}</div>}
        {!err && !rows && <div className="empty">Loading…</div>}
        {!err && rows && filtered.length === 0 && <div className="empty">No hay contrapartes que mostrar.</div>}

        {!err && filtered.length > 0 && (
          <>
            <div className="crm__head">
              <span>Contraparte</span>
              <span>KYC</span>
              <span>Riesgo</span>
              <span>Credit (pending / limit)</span>
              <span style={{ textAlign: 'right' }}>Docs</span>
            </div>
            {filtered.map((c) => {
              const pct = c.creditLimitMinor && c.creditLimitMinor > 0
                ? Math.min(100, (c.outstandingMinor / c.creditLimitMinor) * 100)
                : 0;
              return (
                <button key={c.id} className="crm__row" onClick={() => nav(`/relaciones/${c.id}`)}>
                  <span className="cp-name">
                    <span className="cp-avatar" style={{ background: tone(c.id) }}>{initials(c.name)}</span>
                    <span className="cp-name__main">
                      <span className="cp-name__title">{c.name}</span>
                      <span className="cp-kinds">
                        {c.kindLabels.slice(0, 3).map((k, i) => <span key={i} className="cp-kind">{k}</span>)}
                        {c.country && <span className="cp-kind">{c.country}</span>}
                      </span>
                    </span>
                  </span>
                  <span><span className={`pill pill--${kycTone(c.kycStatus)}`}><span className="pill__dot" />{c.kycLabel}</span></span>
                  <span><span className={`pill pill--${riskTone(c.riskLevel)}`}><span className="pill__dot" />{RISK_LABEL[c.riskLevel] ?? c.riskLevel}</span></span>
                  <span className="credit">
                    {c.creditLimitMinor && c.creditLimitMinor > 0 ? (
                      <>
                        <span className="credit__amt" style={{ color: c.creditExceeded ? 'var(--clay)' : undefined }}>
                          {amount(c.outstandingMinor, c.currency)} / {amount(c.creditLimitMinor, c.currency)}
                        </span>
                        <span className="credit__bar"><span className={`credit__fill${c.creditExceeded ? ' credit__fill--over' : ''}`} style={{ width: `${pct}%` }} /></span>
                      </>
                    ) : (
                      <span className="credit__none">No limit set</span>
                    )}
                  </span>
                  <span className="num" style={{ textAlign: 'right', fontSize: 13 }}>{c.documents}</span>
                </button>
              );
            })}
          </>
        )}
      </div>

      {creating && <CreateModal defaultCurrency={company?.currency ?? 'AED'} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }} />}
    </main>
  );
}
